import { rewardItemSchema, sparseRangeSchema } from './event-shared-object.schema';

import { countryCodeSchema } from './countries';
import * as z from 'zod/v4';
import { GameModeEnums, RewardItemEnums, StringEnum, UserTargetEnums } from './types';

const ABGroupTargetSchema = z.object({ relationId: z.string() });

const ONE_OF_INC_EX_ERR = 'at least one of include or exclude should be defined';
// TODO: add custom regex
const adCampaignTargetSchema = z
  .object({
    include: z.string().optional(),
    exclude: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.exclude && !val.include) {
      ctx.addIssue({ path: ['include'], code: 'custom', message: ONE_OF_INC_EX_ERR });
      ctx.addIssue({ path: ['exclude'], code: 'custom', message: ONE_OF_INC_EX_ERR });
    }
  });

const buildSourceTargetSchema = (vals?: StringEnum) =>
  z
    .object({
      include: (vals ? z.array(z.enum(vals)) : z.array(z.string())).optional(),
      exclude: (vals ? z.array(z.enum(vals)) : z.array(z.string())).optional(),
    })
    .superRefine((val, ctx) => {
      if (!val.exclude?.length && !val.include?.length) {
        ctx.addIssue({ path: ['include'], code: 'custom', message: ONE_OF_INC_EX_ERR });
        ctx.addIssue({ path: ['exclude'], code: 'custom', message: ONE_OF_INC_EX_ERR });
      }
    });
const countriesTargetSchema = z.object({ countries: z.array(countryCodeSchema) });

export const PURCHASE_CONSTRAINT_ENUM = [
  'can_buy',
  'cant_buy',
  'has_bought',
  'hasnt_bought',
] as const;
const purchaseTargetSchema = z.object({
  constraint: z.enum(PURCHASE_CONSTRAINT_ENUM),
  package: z.number().int(),
  count: z.number().nonnegative().int().optional(),
});

export const RELATION_ENUM_VALUES = ['ge', 'le', 'eq', 'gt', 'lt'] as const;
export const relationEnumSchema = z.enum(RELATION_ENUM_VALUES);
export const relationTargetSchema = z.object({
  relation: relationEnumSchema,
  value: z.number(),
});

const playerGameItemCountTargetSchema = (enums?: RewardItemEnums) =>
  relationTargetSchema.and(rewardItemSchema(enums));

const eventParticipatedTargetSchema = z.object({
  eventId: z.uuid(),
  amount: z.number().nonnegative().int(),
});
const eventViewedTargetSchema = z.object({
  eventId: z.uuid(),
  relation: relationEnumSchema,
  duration: z.iso.duration(),
});

const installDateTargetSchema = z.object({
  relation: relationEnumSchema,
  dateTime: z.iso.datetime(),
});

const appVersionTargetSchema = z.object({
  relation: relationEnumSchema,
  version: z
    .string()
    .regex(
      /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/,
    ),
});

const maxPricePointPurchasedTargetSchema = (vals?: StringEnum) =>
  z.object({
    relation: relationEnumSchema,
    value: vals ? z.enum(vals) : z.string(),
  });

const hasActiveCardTargetSchema = (vals?: StringEnum) =>
  z.object({
    value: vals ? z.enum(vals) : z.string(),
  });

const windowedPurchaseTargetSchema = relationTargetSchema.and(
  z.object({
    windowLength: z.iso.duration(),
  }),
);
const hardCurrencyBurnTargetSchema = (vals?: StringEnum) =>
  relationTargetSchema.and(
    z.object({
      category: (vals ? z.enum(vals) : z.string()).optional(),
    }),
  );

const questCompletedTargetSchema = (vals?: StringEnum) =>
  z.object({
    questId: vals ? z.enum(vals) : z.string(),
  });

const combatTargetSchema = (vals?: StringEnum) =>
  z
    .object({
      combatTag: vals ? z.enum(vals) : z.string(),
    })
    .and(relationTargetSchema);

const gameModeOpenTargetSchema = (enums?: GameModeEnums) =>
  z.object({
    gameMode: enums?.GameMode ? z.enum(enums?.GameMode) : z.string(),
    combatTag: (enums?.CombatTag ? z.enum(enums?.CombatTag) : z.string()).optional(),
  });

const gameModeStatTargetSchema = (enums?: GameModeEnums) =>
  z
    .object({
      gameMode: enums?.GameMode ? z.enum(enums?.GameMode) : z.string(),
      combatTag: (enums?.CombatTag ? z.enum(enums?.CombatTag) : z.string()).optional(),
      gameModeStatType: enums?.GameModeStatType ? z.enum(enums?.GameModeStatType) : z.string(),
    })
    .and(relationTargetSchema);

const eventEndedTargetSchema = z.object({
  eventId: z.uuid(),
});

const heroOwnedTargetSchema = (vals?: StringEnum) =>
  z.object({
    type: vals ? z.enum(vals) : z.string(),
    stars: z.number().int().min(0).max(5),
  });

export const TARGET_KEYS = [
  'servers',
  'ABGroup',
  'heroOwned',
  'adCampaign',
  'eventLeaderboardLeague',
  'countries',
  'buildSource',
  'purchase',
  'playerGameItemCount',
  'eventParticipated',
  'eventViewed',
  'playerId',
  'externalContentDownloaded',
  'tutorialCompletion',
  'windowedPurchase',
  'hardCurrencyBurn',
  'questCompleted',
  'campaignStage',
  'daysSinceLogin',
  'daysSinceSpend',
  'purchaseCount',
  'maxPricePointPurchased',
  'hasActiveCard',
  'playerLevel',
  'wallLevel',
  'playerAge',
  'playerSpend',
  'relic',
  'swarmStage',
  'dailyDungeonStage',
  'gameModeOpen',
  'gameModeStat',
  'guildLevel',
  'guildBossTier',
  'installDate',
  'appVersion',
  'eventEnded',
  'playerWeek',
  'leaderboardEventCount',
  'dayOfWeek',
] as const;

export type TargetKeys = (typeof TARGET_KEYS)[number];

const weekDaysSchema = z.object({
  sunday: z.boolean(),
  monday: z.boolean(),
  tuesday: z.boolean(),
  wednesday: z.boolean(),
  thursday: z.boolean(),
  friday: z.boolean(),
  saturday: z.boolean(),
});

const dayOfWeekTargetSchema = (enums?: UserTargetEnums) =>
  z
    .object({
      days: weekDaysSchema,
      dayDefinition: (enums ? z.enum(enums.DayDefinition) : z.string()).default('dailyReset'),
      startTime: z.iso.time().optional(),
    })
    .superRefine((val, ctx) => {
      if (val.dayDefinition === 'specificTime' && !val.startTime) {
        ctx.addIssue({
          path: ['startTime'],
          code: 'custom',
          message: 'Start time is required for specificTime',
        });
      }
    });

export const targetSchema = (enums?: UserTargetEnums) =>
  z.union([
    z.object({ eventLeaderboardLeague: z.object({ eventId: z.uuid(), league: z.number() }) }),
    z.object({ servers: sparseRangeSchema }),
    z.object({ ABGroup: ABGroupTargetSchema }),
    z.object({ heroOwned: heroOwnedTargetSchema(enums?.HeroType) }),
    z.object({ adCampaign: adCampaignTargetSchema }),
    z.object({ countries: countriesTargetSchema }),
    z.object({ buildSource: buildSourceTargetSchema(enums?.BuildSource) }),
    z.object({ purchase: purchaseTargetSchema }),
    z.object({ playerGameItemCount: playerGameItemCountTargetSchema(enums) }),
    z.object({ eventParticipated: eventParticipatedTargetSchema }),
    z.object({ eventViewed: eventViewedTargetSchema }),
    z.object({ playerId: z.object({ ids: z.array(z.uuid()) }) }),
    z.object({ externalContentDownloaded: z.boolean() }),
    z.object({ tutorialCompletion: z.boolean() }),
    z.object({ windowedPurchase: windowedPurchaseTargetSchema }),
    z.object({ hardCurrencyBurn: hardCurrencyBurnTargetSchema(enums?.ResourceBurnReason) }),
    z.object({ questCompleted: questCompletedTargetSchema(enums?.QuestID) }),
    z.object({ campaignStage: relationTargetSchema }),
    z.object({ daysSinceLogin: relationTargetSchema }),
    z.object({ daysSinceSpend: relationTargetSchema }),
    z.object({ purchaseCount: relationTargetSchema }),
    z.object({
      maxPricePointPurchased: maxPricePointPurchasedTargetSchema(enums?.IAPProduct),
    }),
    z.object({ hasActiveCard: hasActiveCardTargetSchema(enums?.MonthlyCardType) }),
    z.object({ playerLevel: relationTargetSchema }),
    z.object({ wallLevel: relationTargetSchema }),
    z.object({ playerAge: relationTargetSchema }),
    z.object({ playerSpend: relationTargetSchema }),
    z.object({ relic: combatTargetSchema(enums?.CombatTag) }),
    z.object({ dailyDungeonStage: combatTargetSchema(enums?.CombatTag) }),
    z.object({ swarmStage: relationTargetSchema }),
    z.object({ guildLevel: relationTargetSchema }),
    z.object({ guildBossTier: relationTargetSchema }),
    z.object({ gameModeOpen: gameModeOpenTargetSchema(enums) }),
    z.object({ gameModeStat: gameModeStatTargetSchema(enums) }),
    z.object({ installDate: installDateTargetSchema }),
    z.object({ appVersion: appVersionTargetSchema }),
    z.object({ eventEnded: eventEndedTargetSchema }),
    z.object({ playerWeek: relationTargetSchema }),
    z.object({ leaderboardEventCount: relationTargetSchema }),
    z.object({ dayOfWeek: dayOfWeekTargetSchema(enums) }),
  ]);

export const userBaseTargetSchema = (enums?: UserTargetEnums) =>
  z.union([z.object({ not: targetSchema(enums) }), targetSchema(enums)]);
export type SingleTarget = z.infer<ReturnType<typeof userBaseTargetSchema>>;

export type UserTargeting =
  | {
      and: ({ and: SingleTarget[] } | { or: SingleTarget[] } | SingleTarget)[];
    }
  | {
      or: (
        | { and: (SingleTarget | { or: SingleTarget[] })[] }
        | { or: SingleTarget[] }
        | SingleTarget
      )[];
    };

export const userTargetSchema = (enums?: UserTargetEnums): z.ZodType<UserTargeting> =>
  z.union([
    z
      .object({
        and: z
          .union([
            z
              .object({
                or: userBaseTargetSchema(enums).array(),
              })
              .strict(),
            z
              .object({
                and: userBaseTargetSchema(enums).array(),
              })
              .strict(),
            userBaseTargetSchema(enums),
          ])
          .array(),
      })
      .strict(),
    z
      .object({
        or: z
          .union([
            z
              .object({
                or: userBaseTargetSchema(enums).array(),
              })
              .strict(),
            z
              .object({
                and: z
                  .union([
                    userBaseTargetSchema(enums),
                    z
                      .object({
                        or: userBaseTargetSchema(enums).array(),
                      })
                      .strict(),
                  ])
                  .array(),
              })
              .strict(),
            userBaseTargetSchema(enums),
          ])
          .array(),
      })
      .strict(),
  ]);
